import { z } from 'zod';
import { decimalSchema } from './prisma-zod';
import { CrmStageTypeEnum } from './crm-stage';

// Lead sort field enum
export enum LeadSortFieldEnum {
  NAME = 'name',
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  EMAIL = 'email',
  STAGE = 'stage',
  STAGE_TYPE = 'stageTypeId',
  CREATED_AT = 'createdAt',
  UPDATED_AT = 'updatedAt',
}

// Lead schema
export const LeadSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().email(),
  phone: z.string(),
  company: z.string(),
  title: z.string(),
  website: z.string().nullable().optional(),
  linkedinUrl: z.string().nullable().optional(),
  industry: z.string().nullable().optional(),
  companySize: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  timezone: z.string().nullable().optional(),
  referredBy: z.string().nullable().optional(),
  stage: z.string().default('new'), // References CrmStage.stageId
  stageTypeId: z.nativeEnum(CrmStageTypeEnum).nullable().optional(), // Denormalized for filtering
  source: z.string(),
  score: decimalSchema,
  assignedToId: z.string().uuid(),
  notes: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),
  customFields: z.record(z.string(), z.any()).optional(),
});

export type Lead = z.infer<typeof LeadSchema>;

// Create Lead schema
export const CreateLeadSchema = LeadSchema.omit({
  id: true,
  organizationId: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  assignedToId: true,
}).extend({
  address: z.any().optional(),
  name: z.string().optional(),
  customFields: z.record(z.string(), z.any()).optional(),
});

export type CreateLead = z.infer<typeof CreateLeadSchema>;

// Update Lead schema
export const UpdateLeadSchema = CreateLeadSchema.partial().extend({
  customFields: z.record(z.string(), z.any()).optional(),
});

export type UpdateLead = z.infer<typeof UpdateLeadSchema>;

// Lead response schema
export const LeadResponseSchema = z.object({
  data: LeadSchema,
  message: z.string().optional(),
});

export type LeadResponse = z.infer<typeof LeadResponseSchema>;

// Leads response schema
export const LeadsResponseSchema = z.object({
  data: z.array(LeadSchema),
  total: z.number().optional(),
  message: z.string().optional(),
});

export type LeadsResponse = z.infer<typeof LeadsResponseSchema>; 